import { useMemo } from "react"
import { useQuery } from "urql"

import type { DestinationOverride } from "../schemas/overrideDestinationSchema"

import type {
  GetAvailableWarehouseShelvingsQuery,
  GoodsReceiptPallet,
  WarehouseShelving,
  WarehouseShelvingBinSize,
  WarehouseShelvingQueryListArgs,
} from "@/graphql/codegen/graphql"
import { GetAvailableWarehouseShelvingsDocument } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface UseWarehouseShelvingOptionsParams {
  warehouseId?: string
  binSize?: WarehouseShelvingBinSize | null
  aisleCode?: string | null
  sectionCode?: string | null
  currentPalletId?: string
  pallets?: GoodsReceiptPallet[]
  destinationOverrides?: Map<string, DestinationOverride>
  currentDestination?: Partial<WarehouseShelving>
}

interface UseWarehouseShelvingOptionsReturn {
  aisleOptions: Option<string>[]
  shelfOptions: Option<string>[]
  binOptions: Option<number>[]
  shelvings: WarehouseShelving[]
  isFetching: boolean
}

const LIMIT = 10000

export const useWarehouseShelvingOptions = ({
  warehouseId,
  binSize,
  aisleCode,
  sectionCode,
  currentPalletId,
  pallets,
  destinationOverrides,
  currentDestination,
}: UseWarehouseShelvingOptionsParams): UseWarehouseShelvingOptionsReturn => {
  const [{ data, fetching }] = useQuery<GetAvailableWarehouseShelvingsQuery, WarehouseShelvingQueryListArgs>({
    query: GetAvailableWarehouseShelvingsDocument,
    variables: {
      input: {
        filter: {
          warehouseAreaId: warehouseId,
          isAvailable: true,
          isDoNotUse: false,
          binSize: binSize ? [binSize] : undefined,
        },
        limit: LIMIT,
        offset: 0,
      },
    },
    pause: !warehouseId,
  })

  const shelvings = useMemo(() => {
    if (data?.warehouseShelving.list.__typename === "WarehouseShelvingListFailure") {
      console.error("Warehouse shelving query failed:", data.warehouseShelving.list.error)
      return []
    }

    return (data?.warehouseShelving.list.warehouseShelvings ?? []) as WarehouseShelving[]
  }, [data])

  const availableShelvings = useMemo(() => {
    const takenIds = new Set<string>()

    for (const override of destinationOverrides?.values() ?? []) {
      takenIds.add(override.warehouseShelvingId)
    }

    for (const pallet of pallets ?? []) {
      if (pallet.goodsReceiptPalletId !== currentPalletId && pallet.destinationWarehouseShelving?.warehouseShelvingId) {
        takenIds.add(pallet.destinationWarehouseShelving.warehouseShelvingId)
      }
    }

    if (currentDestination?.warehouseShelvingId) {
      takenIds.delete(currentDestination.warehouseShelvingId)
    }

    if (!takenIds.size) {
      return shelvings
    }

    return shelvings.filter((shelving) => !takenIds.has(shelving.warehouseShelvingId))
  }, [shelvings, destinationOverrides, pallets, currentPalletId, currentDestination?.warehouseShelvingId])

  const aisleOptions = useMemo<Option<string>[]>(() => {
    const uniqueAisles = new Set<string>()

    for (const shelving of availableShelvings) {
      if (shelving.aisleCode) {
        uniqueAisles.add(shelving.aisleCode)
      }
    }

    if (currentDestination?.aisleCode && !uniqueAisles.has(currentDestination.aisleCode)) {
      const shouldIncludeCurrent = !binSize || (currentDestination.binSize && currentDestination.binSize === binSize)
      if (shouldIncludeCurrent) {
        uniqueAisles.add(currentDestination.aisleCode)
      }
    }

    return Array.from(uniqueAisles)
      .sort((a: string, b: string): number => {
        const aNum = Number.parseInt(a, 10)
        const bNum = Number.parseInt(b, 10)
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return aNum - bNum
        }
        return a.localeCompare(b)
      })
      .map((aisle) => ({ value: aisle, label: aisle }))
  }, [availableShelvings, currentDestination, binSize])

  const shelfOptions = useMemo<Option<string>[]>(() => {
    if (!aisleCode) return []

    const uniqueShelves = new Set<string>()

    for (const shelving of availableShelvings) {
      if (shelving.aisleCode === aisleCode && shelving.sectionCode) {
        uniqueShelves.add(shelving.sectionCode)
      }
    }

    if (
      currentDestination?.aisleCode === aisleCode &&
      currentDestination?.sectionCode &&
      !uniqueShelves.has(currentDestination.sectionCode)
    ) {
      const shouldIncludeCurrent = !binSize || (currentDestination.binSize && currentDestination.binSize === binSize)
      if (shouldIncludeCurrent) {
        uniqueShelves.add(currentDestination.sectionCode)
      }
    }

    return Array.from(uniqueShelves)
      .sort((a, b) => a.localeCompare(b))
      .map((shelf) => ({ value: shelf, label: shelf }))
  }, [availableShelvings, aisleCode, currentDestination, binSize])

  const binOptions = useMemo<Option<number>[]>(() => {
    if (!aisleCode || !sectionCode) return []

    const uniqueBins = new Set<number>()

    for (const shelving of availableShelvings) {
      if (
        shelving.aisleCode === aisleCode &&
        shelving.sectionCode === sectionCode &&
        shelving.binNumber !== null &&
        shelving.binNumber !== undefined
      ) {
        uniqueBins.add(shelving.binNumber)
      }
    }

    if (
      currentDestination?.aisleCode === aisleCode &&
      currentDestination?.sectionCode === sectionCode &&
      currentDestination?.binNumber !== null &&
      currentDestination?.binNumber !== undefined &&
      !uniqueBins.has(currentDestination.binNumber)
    ) {
      const shouldIncludeCurrent = !binSize || (currentDestination.binSize && currentDestination.binSize === binSize)
      if (shouldIncludeCurrent) {
        uniqueBins.add(currentDestination.binNumber)
      }
    }

    return Array.from(uniqueBins)
      .sort((a, b) => a - b)
      .map((bin) => ({ value: bin, label: bin.toString() }))
  }, [availableShelvings, aisleCode, sectionCode, currentDestination, binSize])

  return {
    aisleOptions,
    shelfOptions,
    binOptions,
    shelvings,
    isFetching: fetching,
  }
}
